// CTV-800: the staff Waymark studio, at /admin/studio.
//
// Where CTVBuyer staff make Waymark commercials that belong to no campaign,
// on CTVBuyer's own Waymark partner, ending at a downloaded file. See
// docs/adr/CTV-800-staff-studio-commercials.md and the "Staff Waymark studio
// (CTV-800)" glossary in CONTEXT.md.
//
// Auth mirrors WaymarkCredentialsPage:
//   - signed out                -> redirect to /admin (single sign-in surface)
//   - signed in, not super_admin -> redirect to /admin
//   - signed in, super_admin     -> render
// The redirect is UI shape, not the security boundary; every endpoint behind
// this page carries @Roles('super_admin') of its own.
//
// Shape, after the 2026-08-27 cross-write incident (see useStudioCommercials):
//   - ONE shared list for every super_admin, with who made what.
//   - Each staff member's page works on at most ONE commercial, shown as the
//     wizard's stage pills + ledger (staff copy, neutral accent). Generate is
//     gated the way the wizard gates Make another: while a run is in flight
//     in this tab the working card takes the Make card's slot, and the Make
//     card returns the moment the run stops (draft ready, done, or failed),
//     so two Waymark runs never overlap on one page.
//   - Finished commercials in the list preview in place: a first-frame still
//     that expands into an inline player. A plain <video> never touches the
//     Waymark session, so previews work while a generation runs.
//   - Nothing is active on page load (no SDK iframe, no Waymark login) unless
//     this staff member's own generation is still in flight.
//   - Done = the broadcast file exists. From then on the row stays in the
//     Studio for good: Customize reopens the editor on its own account (a
//     new Finish replaces the file), Download hands the file over.
//   - Only ever one SDK instance on the page: a listed row's Customize
//     switches the provider onto that row, it never opens a side session.

import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Clapperboard,
  Download,
  Loader2,
  Globe,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { useAuth } from '../../components/self-service/auth/useAuth';
import Banner from '../../components/self-service/shared/Banner';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import { WaymarkProvider } from '../../components/self-service/waymark/WaymarkProvider';
import { useWaymarkOptional } from '../../components/self-service/waymark/context';
import {
  FAILED_STATUSES,
  isDone,
  isInFlight,
  useStudioCommercials,
  type StudioCommercial,
  type StudioCommercialsState,
} from '../../components/self-service/waymark/useStudioCommercials';
import {
  STUDIO_SLUG,
  studioTransport,
} from '../../components/self-service/waymark/studioTransport';
import { looksLikeWebsiteUrl } from '../../components/self-service/waymark/normalizeUrl';
import {
  LedgerMarker,
  LedgerTimeline,
  StagePills,
  formatClock,
  ledgerStageFor,
} from '../../components/self-service/waymark/ledger';
import {
  STUDIO_ACCENT,
  STUDIO_LEDGER_ROWS,
  STUDIO_STAGE_PILLS,
} from '../../components/self-service/waymark/studioLedgerCopy';
import {
  formatAdSpecViolations,
  validateAdSpecs,
} from '../../components/self-service/shared/validateAdSpecs';

const StudioPage: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  // Gated: everyone below is redirected, and the list would be a guaranteed
  // 401/403 for them. The gate here is UI shape; the endpoints enforce it.
  const isSuperAdmin = isAuthenticated && user?.rawRole === 'super_admin';
  const studio = useStudioCommercials(isSuperAdmin, isSuperAdmin ? (user?.id ?? null) : null);

  if (!isAuthenticated || !user) {
    return <Navigate to="/admin" state={{ from: location.pathname + location.search }} replace />;
  }
  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  return <StudioBody studio={studio} />;
};

const StudioBody: React.FC<{ studio: StudioCommercialsState }> = ({ studio }) => {
  const { active, activeId, activeInitial, recordState } = studio;

  // ONE provider, on the active studio commercial, carrying the Studio's
  // authenticated transport so its access token never reaches the browser.
  // With no active commercial it provides a null context and mounts no SDK.
  //
  // Deliberately NOT keyed on activeId. The provider switches commercials IN
  // PLACE by design (WaymarkProvider.tsx) so the surface's staged state
  // below it survives; keying it here remounted the whole subtree the moment
  // Generate created a row, wiping the pending generation before it started.
  return (
    <WaymarkProvider
      slug={STUDIO_SLUG}
      commercialToken={null}
      commercialId={activeId}
      transport={activeId ? studioTransport(activeId) : undefined}
      surface="studio"
      commercialOrdinal={1}
      initial={activeInitial}
      onStateChange={recordState}
    >
      <StudioShell studio={studio} active={active} />
    </WaymarkProvider>
  );
};

type PendingAction = { id: string; action: 'customize' | 'restart' };

const StudioShell: React.FC<{
  studio: StudioCommercialsState;
  active: StudioCommercial | null;
}> = ({ studio, active }) => {
  const waymark = useWaymarkOptional();
  const [notice, setNotice] = React.useState<string | null>(null);
  const [specWarning, setSpecWarning] = React.useState<string | null>(null);
  // Kept apart from specWarning on purpose: the spec banner says the file
  // downloaded anyway, which is exactly wrong when the download itself failed.
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const bannersRef = React.useRef<HTMLDivElement | null>(null);

  // The banners sit at the top of the page, but Download lives on rows near
  // the bottom, so the answer to a click could land entirely off-screen. Bring
  // it into view whenever one appears.
  const feedback = notice ?? specWarning ?? downloadError;
  React.useEffect(() => {
    if (!feedback) return;
    // Optional-called: jsdom (and some embedded webviews) do not implement it,
    // and a missing scroll must never break the feedback it was meant to reveal.
    bannersRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [feedback]);

  // The gate: this tab's one live run, read from the provider once it is on
  // the row and from the row itself before that. Same rule as the wizard's
  // Make another (one run at a time), which is what keeps two Waymark runs,
  // and two Waymark logins, from ever overlapping on one page.
  // "Live" = the provider is mounted on this very row and has hydrated it.
  // Only then are its values the truth; before that (or for a stale context
  // from a previous row) the row snapshot is.
  const live = !!active && !!waymark && waymark.commercialId === active.id && !waymark.isHydrating;
  const liveStatus = live ? waymark!.status : (active?.status ?? 'idle');
  const isFinalizing = live ? waymark!.isFinalizing : false;
  const busy = !!active && (isInFlight({ status: liveStatus }) || isFinalizing);

  // List actions that need the provider on their row first (Customize,
  // Try again, Start): switch, then act once the provider has hydrated it.
  const pendingRef = React.useRef<PendingAction | null>(null);
  const { activeId } = studio;
  React.useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || !waymark) return;
    // Both must agree: the page has switched AND the provider has hydrated
    // that row. A stale pending action for a row the user moved off is dropped.
    if (pending.id !== activeId) {
      pendingRef.current = null;
      return;
    }
    if (waymark.commercialId !== pending.id || waymark.isHydrating) return;
    pendingRef.current = null;
    if (pending.action === 'customize') void waymark.openEditor();
    else waymark.restartGeneration();
  }, [waymark, activeId]);

  const workOn = React.useCallback(
    (id: string, action: PendingAction['action'] | null) => {
      if (busy) return;
      pendingRef.current = action ? { id, action } : null;
      studio.setActiveId(id);
    },
    [busy, studio],
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-10 md:py-14">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-[0.14em] mb-4">
            <Clapperboard className="w-3 h-3" />
            Studio
          </div>
          <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight">
            Commercials for direct clients
          </h1>
          <p className="mt-3 text-[15px] text-slate-500 leading-relaxed max-w-2xl">
            Make a commercial with Waymark for a client who has no campaign here, customize it,
            and download the broadcast file. These are billed to CTVBuyer's Waymark plan, never a
            station's, and they never appear in anyone's campaign.
          </p>
        </div>

        <div ref={bannersRef} />
        {studio.error && (
          <Banner tone="warn" label="Something went wrong" className="mb-6">
            {studio.error}
          </Banner>
        )}
        {downloadError && (
          <Banner
            tone="warn"
            label="Download failed"
            className="mb-6"
            rightSlot={<DismissButton onClick={() => setDownloadError(null)} />}
          >
            {downloadError}
          </Banner>
        )}
        {specWarning && (
          <Banner
            tone="caution"
            label="Check the specs"
            className="mb-6"
            rightSlot={<DismissButton onClick={() => setSpecWarning(null)} />}
          >
            {specWarning} It downloaded anyway, in case that is what you wanted, but it will
            not pass an ad server.
          </Banner>
        )}
        {notice && (
          <Banner
            tone="success"
            label="Downloaded"
            className="mb-6"
            rightSlot={<DismissButton onClick={() => setNotice(null)} />}
          >
            {notice}
          </Banner>
        )}

        {/* The gate: while a run is in flight the working card takes this
            slot, and the Make card is back the moment the run stops. */}
        {!busy && <GeneratePanel studio={studio} />}

        <WorkingOnPanel
          studio={studio}
          active={active}
          onNotice={setNotice}
          onSpecWarning={setSpecWarning}
          onDownloadError={setDownloadError}
        />

        <StudioList
          studio={studio}
          busy={busy}
          onCustomize={(id) => workOn(id, 'customize')}
          onResume={(id) => workOn(id, null)}
          onRestart={(id) => workOn(id, 'restart')}
          onNotice={setNotice}
          onSpecWarning={setSpecWarning}
          onDownloadError={setDownloadError}
        />
      </div>
    </div>
  );
};

const DismissButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Dismiss"
    className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
  >
    ×
  </button>
);

/**
 * Generate. Explicit; it never starts on its own, and loading this page
 * fires no generation. Not rendered at all while this tab has a run in
 * flight (StudioShell swaps the working card into its slot).
 */
const GeneratePanel: React.FC<{ studio: StudioCommercialsState }> = ({ studio }) => {
  const [companyName, setCompanyName] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);
  // Which fields the last submit rejected, so the inputs themselves show it.
  const [badFields, setBadFields] = React.useState<{ name?: boolean; site?: boolean }>({});
  // No client-side kickoff here on purpose. A studio commercial created with
  // a website is born in `topic_searching`, and WaymarkProvider's resume
  // branch starts it after hydration. Doing it from this panel raced that
  // hydration, because a child's effects run before its parent's.

  const onGenerate = async () => {
    const name = companyName.trim();
    const url = website.trim();
    // Both at once: a two-field form should never cost two round trips.
    const bad = { name: !name, site: !looksLikeWebsiteUrl(url) };
    if (bad.name || bad.site) {
      setBadFields(bad);
      setFormError(
        bad.name && bad.site
          ? 'Enter the client business and their website, for example acmefencing.com.'
          : bad.name
            ? 'Enter the client business name.'
            : 'Enter the client website, for example acmefencing.com.',
      );
      return;
    }
    setBadFields({});
    setFormError(null);
    const withProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const created = await studio.create({ companyName: name, sourceUrl: withProtocol });
    if (!created) return;
    setCompanyName('');
    setWebsite('');
  };

  const disabled = studio.isCreating;

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 mb-6"
      data-testid="studio-generate"
    >
      <h2 className="text-base font-semibold text-slate-900">Make a commercial</h2>
      <p className="mt-1 text-sm text-slate-500">
        Waymark reads the client's website to write the first draft. It is set up under your
        name and work email, not the client's, so the client gets no email from Waymark.
      </p>

      {formError && (
        <Banner tone="warn" label="Missing details" compact className="mt-4">
          {formError}
        </Banner>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Client business</span>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={disabled}
            data-testid="studio-company-name"
            aria-invalid={badFields.name ? true : undefined}
            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 ${
              badFields.name ? 'border-red-400 bg-red-50' : 'border-slate-300'
            }`}
            placeholder="Acme Fencing"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Client website</span>
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            disabled={disabled}
            data-testid="studio-website"
            aria-invalid={badFields.site ? true : undefined}
            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 ${
              badFields.site ? 'border-red-400 bg-red-50' : 'border-slate-300'
            }`}
            placeholder="acmefencing.com"
          />
        </label>
      </div>

      <div className="mt-5">
        <PrimaryButton
          onClick={() => void onGenerate()}
          disabled={disabled}
          data-testid="studio-generate-button"
        >
          {studio.isCreating ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Starting…
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Generate
            </span>
          )}
        </PrimaryButton>
      </div>
    </section>
  );
};

// Past this, a generation is unusual enough to say so. Waymark gives no
// progress signal and no ETA, so the honest thing is to report elapsed time
// and stop pretending a spinner is information.
const SLOW_AFTER_MS = 8 * 60 * 1000;

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m > 0 ? `${m}m ${String(sec).padStart(2, '0')}s` : `${sec}s`;
}

/**
 * Elapsed time in the current generation, ticking once a second.
 *
 * `since` is when the provider OBSERVED this run start, so a page opened
 * mid-generation shows nothing rather than inventing a start time. Same rule
 * the buyer ledger uses.
 */
const ElapsedNote: React.FC<{ since: number | null }> = ({ since }) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (since == null) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [since]);

  if (since == null) {
    return (
      <span className="text-slate-400" data-testid="studio-elapsed">
        running (started before this page was opened)
      </span>
    );
  }
  const ms = now - since;
  const slow = ms > SLOW_AFTER_MS;
  return (
    <span
      className={slow ? 'text-amber-700' : 'text-slate-500'}
      data-testid="studio-elapsed"
    >
      {formatElapsed(ms)} so far
      {slow ? ', longer than usual' : ''}
    </span>
  );
};

function hostOf(url: string | null): string {
  if (!url) return 'the website';
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(
      /^www\./,
      '',
    );
  } catch {
    return url;
  }
}

function madeBy(c: StudioCommercial): string {
  return `Made by ${c.createdByName ?? 'staff'} · ${new Date(c.createdAt).toLocaleDateString()}`;
}

const DoneChip: React.FC = () => (
  <span
    className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700"
    data-testid="studio-done-chip"
  >
    Done
  </span>
);

/** The commercial being worked on: the one the provider is mounted on. */
const WorkingOnPanel: React.FC<{
  studio: StudioCommercialsState;
  active: StudioCommercial | null;
  onNotice: (v: string | null) => void;
  onSpecWarning: (v: string | null) => void;
  onDownloadError: (v: string | null) => void;
}> = ({ studio, active, onNotice, onSpecWarning, onDownloadError }) => {
  // Null while nothing is active; every read below falls back to the row.
  const waymark = useWaymarkOptional();
  const [isOpening, setIsOpening] = React.useState(false);
  // Change website: an inline edit of the source URL on the SAME commercial.
  // A new URL is a fresh run (the provider bumps the run nonce and persists
  // `restart`); the same URL is a plain Start over.
  const [siteDraft, setSiteDraft] = React.useState<string | null>(null);
  const [siteError, setSiteError] = React.useState<string | null>(null);
  // The gap between clicking Finish in the editor and the broadcast file
  // landing. Declared with the other hooks, ABOVE the early return. Only the
  // provider mounted on THIS row can be finalizing it.
  const isFinalizing =
    !!active && !!waymark && waymark.commercialId === active.id ? waymark.isFinalizing : false;
  const [finalizingSince, setFinalizingSince] = React.useState<number | null>(null);
  React.useEffect(() => {
    setFinalizingSince(isFinalizing ? Date.now() : null);
  }, [isFinalizing]);

  if (!active) {
    if (!studio.isLoaded) {
      return (
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the Studio…
        </div>
      );
    }
    return null;
  }

  // Once the provider is live on this row, its state is the ONLY truth: a
  // per-field `??` fallback resurrected the row's stored render URL exactly
  // when the provider had cleared its own on Finish (the old file is stale
  // from that moment), so the card offered the previous file as Download
  // while "Making the broadcast file" was still running.
  const live = !!waymark && waymark.commercialId === active.id && !waymark.isHydrating;
  const status = live ? waymark!.status : active.status;
  const renderedUrl = live ? waymark!.renderedUrl : active.renderedUrl;
  const videoId = live ? waymark!.videoId : active.videoId;
  const sourceUrl = live ? waymark!.sourceUrl : active.sourceUrl;
  const errorMessage = live ? waymark!.errorMessage : active.errorMessage;
  const stageEnteredAt = live ? waymark!.stageEnteredAt : {};

  const isFailed = FAILED_STATUSES.has(status);
  const inProgress = isInFlight({ status });
  const stage = ledgerStageFor({ status, isFinalizing, renderedUrl });
  const done = stage === 'ready';
  // A videoId is NOT proof the editor can open: Waymark hands one back the
  // moment it ACCEPTS the generation job, minutes before the video exists.
  // `ready` is the real signal, and the broadcast render must not be in
  // flight either.
  const canCustomize = status === 'ready' && !!videoId && !isFinalizing;
  // The provider stamps 'analyzing' when a run kicks off IN THIS SESSION, so a
  // page opened mid-generation correctly reports that it cannot tell.
  const runStartedAt = stageEnteredAt.analyzing ?? null;
  const host = hostOf(sourceUrl);
  const startedAt = stageEnteredAt.analyzing;
  const readyAt = stageEnteredAt.ready;
  const madeInMin =
    startedAt != null && readyAt != null && readyAt > startedAt
      ? Math.max(1, Math.round((readyAt - startedAt) / 60000))
      : null;
  // The card is never a dead end: while Waymark works (and after), the
  // staff member can restart, point the same commercial at another website,
  // or leave this one to finish untracked and start a different one. Only
  // the broadcast render after Finish is left alone: that is their own edit
  // rendering, minutes from done.
  const canRedirect = !!waymark && !!sourceUrl && !isFinalizing;

  const submitSite = () => {
    if (!waymark || siteDraft == null) return;
    const trimmed = siteDraft.trim();
    if (!looksLikeWebsiteUrl(trimmed)) {
      setSiteError('Enter a website, for example acmefencing.com.');
      return;
    }
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    setSiteDraft(null);
    setSiteError(null);
    if (withProtocol === sourceUrl) waymark.restartGeneration();
    else waymark.startGenerationForUrl(withProtocol);
  };

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 mb-6"
      data-testid="studio-active"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Working on
          </div>
          <h2 className="mt-1 text-lg font-semibold text-slate-900 flex items-center gap-2">
            <span className="truncate">{active.companyName ?? 'Untitled commercial'}</span>
            {done && <DoneChip />}
          </h2>
          <p className="mt-1 text-sm text-slate-500 truncate">
            {madeBy(active)}
            {sourceUrl ? ` · ${sourceUrl}` : ''}
          </p>
          {siteDraft != null && (
            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              data-testid="studio-change-website"
              onSubmit={(e) => {
                e.preventDefault();
                submitSite();
              }}
            >
              <input
                value={siteDraft}
                onChange={(e) => setSiteDraft(e.target.value)}
                aria-label="Client website"
                aria-invalid={siteError ? true : undefined}
                autoFocus
                className={`w-72 max-w-full rounded-md border px-3 py-1.5 text-sm ${
                  siteError ? 'border-red-400 bg-red-50' : 'border-slate-300'
                }`}
                data-testid="studio-change-website-input"
              />
              <PrimaryButton type="submit" data-testid="studio-change-website-submit">
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Regenerate
                </span>
              </PrimaryButton>
              <button
                type="button"
                onClick={() => {
                  setSiteDraft(null);
                  setSiteError(null);
                }}
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
                data-testid="studio-change-website-cancel"
              >
                Cancel
              </button>
              {siteError && (
                <span className="basis-full text-xs text-red-600" role="alert">
                  {siteError}
                </span>
              )}
            </form>
          )}
          {stage && (
            <StagePills accentColor={STUDIO_ACCENT} spec={STUDIO_STAGE_PILLS[stage]} className="mt-3" />
          )}
        </div>
        {(inProgress || isFinalizing) && (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400 shrink-0 mt-1" />
        )}
      </div>

      {errorMessage && (
        <Banner tone="warn" label="Waymark said" compact className="mt-4">
          {errorMessage}
          {isFailed ? ' Trying again often clears this.' : ''}
        </Banner>
      )}

      {/* The ledger: what happened (checked rows with observed times), what
          is happening (one explained row), what comes next. Done collapses
          the history to one line above the player, as the wizard does. */}
      {stage && stage !== 'ready' && (
        <div className="mt-4" data-testid="studio-ledger">
          <LedgerTimeline
            accentColor={STUDIO_ACCENT}
            rows={STUDIO_LEDGER_ROWS}
            stage={stage}
            host={host}
            priorCount={0}
            stageEnteredAt={stageEnteredAt}
          />
          {(inProgress || isFinalizing) && (
            <p className="mt-2 text-xs text-slate-400 max-w-lg" data-testid="studio-progress-note">
              <ElapsedNote since={isFinalizing ? finalizingSince : runStartedAt} />
              {' · '}
              Keep this tab open: the result is recorded when this page hears back from
              Waymark, and closing the tab means it is not saved here.
            </p>
          )}
          {stage === 'customize' && (
            <p className="mt-2 text-sm text-slate-500 max-w-lg" data-testid="studio-no-render-yet">
              The first draft is ready. Waymark makes the broadcast file when you click Finish
              in the editor, so Download appears after that.
            </p>
          )}
        </div>
      )}

      {done && (
        <div className="mt-4" data-testid="studio-ready">
          <div className="flex items-start gap-2.5 py-1" data-testid="studio-ready-summary">
            <LedgerMarker kind="done" accentColor={STUDIO_ACCENT} />
            <span className="flex-1 min-w-0 text-[12.5px] font-semibold text-slate-900">
              {madeInMin != null
                ? `Made in ${madeInMin} minute${madeInMin === 1 ? '' : 's'} from ${host}`
                : `Made from ${host}`}
            </span>
            {formatClock(readyAt) && (
              <span className="ml-auto pl-2.5 pt-0.5 text-[10.5px] text-slate-400 tabular-nums whitespace-nowrap">
                {formatClock(readyAt)}
              </span>
            )}
          </div>
          {/* controlsList="nodownload": the browser's own overflow menu offers
              a Download that saves the preview asset, not the broadcast file
              this page exists to hand over. Two Downloads meaning two
              different files is a wrong-file hazard, so the native one goes. */}
          <video
            src={renderedUrl ?? undefined}
            controls
            controlsList="nodownload"
            preload="metadata"
            className="mt-2 w-full max-w-xl rounded-lg border border-slate-200 bg-black"
            data-testid="studio-active-video"
          />
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Done. It stays in the Studio from here: Customize opens the editor again and a new
            Finish replaces this file, Download hands it over.
          </p>
        </div>
      )}

      {!stage && !isFailed && (
        <p className="mt-4 text-sm text-slate-500" data-testid="studio-not-started">
          Not started.
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {canCustomize && (
          // Secondary, deliberately: Download is what this surface exists to
          // reach, so it is the only pill here. Matches the list rows.
          <button
            type="button"
            onClick={async () => {
              if (!waymark) return;
              setIsOpening(true);
              try {
                await waymark.openEditor();
              } finally {
                setIsOpening(false);
              }
            }}
            disabled={isOpening || !waymark}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            data-testid="studio-customize-active"
          >
            {isOpening ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Pencil className="w-4 h-4" />
            )}
            Customize
          </button>
        )}
        {/* While a new render is in flight the only file that exists is the
            previous version, which Finish just superseded; offering it here
            as "the result" is a wrong-file hazard, so Download waits. */}
        {renderedUrl && !isFinalizing && (
          <DownloadButton
            commercial={active}
            renderedUrl={renderedUrl}
            studio={studio}
            onNotice={onNotice}
            onSpecWarning={onSpecWarning}
            onDownloadError={onDownloadError}
            testId="studio-download-active"
          />
        )}
        {/* On a terminal failure Start over is the ONLY way forward, so it
            takes the primary slot there. Otherwise it is the quiet option
            behind Customize and Download, offered while a run is in flight
            too: a restart on the same account is safe (the provider orphans
            the old subscriptions), and a card you can only watch is a dead
            end. Withheld only while the broadcast render after Finish is in
            flight. */}
        {sourceUrl && waymark && isFailed && (
          <PrimaryButton
            onClick={() => waymark.restartGeneration()}
            data-testid="studio-start-over"
          >
            <span className="inline-flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Try again
            </span>
          </PrimaryButton>
        )}
        {canRedirect && !isFailed && (
          <button
            type="button"
            onClick={() => waymark!.restartGeneration()}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
            data-testid="studio-start-over"
          >
            <RotateCcw className="w-4 h-4" /> Start over
          </button>
        )}
        {canRedirect && siteDraft == null && (
          <button
            type="button"
            onClick={() => {
              setSiteDraft(sourceUrl ?? '');
              setSiteError(null);
            }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
            data-testid="studio-change-website-open"
          >
            <Globe className="w-4 h-4" /> Change website
          </button>
        )}
        {(inProgress || isFinalizing) && (
          // Parks this one: Waymark keeps working on it, the list shows it as
          // paused with Resume, and the Make card is back.
          <button
            type="button"
            onClick={() => studio.setActiveId(null)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
            data-testid="studio-start-different"
          >
            <Sparkles className="w-4 h-4" /> Set aside and start another
          </button>
        )}
      </div>
      {(inProgress || isFinalizing) && (
        <p className="mt-2 text-xs text-slate-400 max-w-lg" data-testid="studio-set-aside-note">
          Set aside keeps it at Waymark: it finishes its current step, then waits for Resume.
        </p>
      )}
    </section>
  );
};

/**
 * Export. Fetches the broadcast file, runs the ad-spec probe, saves it, then
 * stamps exported_at server-side.
 *
 * The probe WARNS and never blocks: staff may legitimately want a file that
 * would fail Publica, and the probe is also the only thing that would catch
 * the still-open Waymark fidelity bug (15s renders against duration 30,
 * near-vertical output against 16:9) before a file reaches a paying client.
 */
const DownloadButton: React.FC<{
  commercial: StudioCommercial;
  renderedUrl: string;
  studio: StudioCommercialsState;
  onNotice: (v: string | null) => void;
  onSpecWarning: (v: string | null) => void;
  onDownloadError: (v: string | null) => void;
  testId: string;
  /** Rows use 'secondary' so the working card keeps the page's only pill. */
  variant?: 'primary' | 'secondary';
}> = ({
  commercial,
  renderedUrl,
  studio,
  onNotice,
  onSpecWarning,
  onDownloadError,
  testId,
  variant = 'primary',
}) => {
  const [busy, setBusy] = React.useState(false);

  const onDownload = async () => {
    setBusy(true);
    onNotice(null);
    onSpecWarning(null);
    onDownloadError(null);
    try {
      const res = await fetch(renderedUrl);
      if (!res.ok) {
        onDownloadError(`Could not download the broadcast file (${res.status}). Please try again.`);
        return;
      }
      const blob = await res.blob();

      // Warn, never block.
      try {
        const specs = await validateAdSpecs(blob);
        if (!specs.ok) onSpecWarning(formatAdSpecViolations(specs.violations));
      } catch {
        // A probe that cannot read the file must not stop the download.
      }

      const safeName =
        (commercial.companyName ?? 'commercial')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'commercial';
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${safeName}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);

      // Stamped after the file is actually in hand. Once, never moved.
      const stamp = await apiClient.post<{ exportedAt: string }>(
        `/admin/waymark/studio/commercials/${commercial.id}/export`,
        {},
      );
      studio.recordExported(commercial.id, stamp.exportedAt);
      onNotice(`Saved ${safeName}.mp4.`);
    } catch (e) {
      onDownloadError(e instanceof Error ? e.message : 'Could not download the commercial.');
    } finally {
      setBusy(false);
    }
  };

  const icon = busy ? (
    <Loader2 className="w-3.5 h-3.5 animate-spin" />
  ) : (
    <Download className="w-3.5 h-3.5" />
  );

  if (variant === 'secondary') {
    return (
      <button
        type="button"
        onClick={() => void onDownload()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        data-testid={testId}
      >
        {icon}
        Download
      </button>
    );
  }

  return (
    <PrimaryButton onClick={() => void onDownload()} disabled={busy} data-testid={testId}>
      <span className="inline-flex items-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Download
      </span>
    </PrimaryButton>
  );
};

/** One line on what a listed row is up to. */
function libraryStatus(c: StudioCommercial): string {
  if (isDone(c)) {
    return c.exportedAt
      ? `Done · downloaded ${new Date(c.exportedAt).toLocaleDateString()}`
      : 'Done';
  }
  if (c.status === 'ready') return c.videoId ? 'Needs customizing' : 'Ready';
  // A listed in-flight row is one no tab is following. Waymark finishes the
  // step it is on by itself; the next step needs a browser, so it waits.
  if (isInFlight(c)) return 'Paused here; Waymark finishes its current step, then waits for Resume';
  if (c.status === 'topic_failed') return 'Could not get started';
  if (c.status === 'generation_failed') return 'Stopped while generating';
  return 'Not started';
}

/**
 * Flips true once the element has scrolled near the viewport, and stays
 * true. Lets a long list defer each row's video still until it is needed
 * instead of fetching every file on load. Without IntersectionObserver
 * (jsdom, very old webviews) everything counts as in view.
 */
function useInView<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = React.useRef<T>(null);
  const [inView, setInView] = React.useState(() => typeof IntersectionObserver === 'undefined');
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

const TILE = 'w-32 h-[72px] rounded-md shrink-0 overflow-hidden';

/**
 * The row's tile. A finished commercial shows a first-frame still of its
 * broadcast file (Waymark hands us no poster; a server-made poster can slot
 * in here later, the wizard's receipt rows do the same fallback) and toggles
 * the inline player. A row with no file yet shows its state instead.
 */
const StudioThumb: React.FC<{
  commercial: StudioCommercial;
  expanded: boolean;
  onToggle: () => void;
}> = ({ commercial: c, expanded, onToggle }) => {
  if (c.renderedUrl) {
    return <PreviewTile commercial={c} expanded={expanded} onToggle={onToggle} />;
  }
  const [Icon, label] = isInFlight(c)
    ? [Pause, 'Paused']
    : c.status === 'ready'
      ? [Pencil, 'Draft']
      : FAILED_STATUSES.has(c.status)
        ? [AlertTriangle, 'Failed']
        : [Clapperboard, 'Not started'];
  return (
    <div
      className={`${TILE} bg-slate-100 border border-slate-200 flex flex-col items-center justify-center gap-1 text-slate-400`}
      data-testid={`studio-thumb-${c.id}`}
      data-placeholder={label.toLowerCase()}
      aria-hidden="true"
    >
      <Icon className="w-4 h-4" />
      <span className="text-[10px] font-medium">{label}</span>
    </div>
  );
};

const PreviewTile: React.FC<{
  commercial: StudioCommercial;
  expanded: boolean;
  onToggle: () => void;
}> = ({ commercial: c, expanded, onToggle }) => {
  const [ref, inView] = useInView<HTMLButtonElement>();
  const name = c.companyName ?? 'this commercial';
  return (
    <button
      ref={ref}
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={expanded ? `Close the preview of ${name}` : `Preview ${name}`}
      className={`${TILE} relative bg-black group focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400`}
      data-testid={`studio-thumb-${c.id}`}
    >
      {inView && (
        <video
          src={`${c.renderedUrl}#t=0.5`}
          preload="metadata"
          muted
          playsInline
          tabIndex={-1}
          aria-hidden="true"
          className="w-full h-full object-cover pointer-events-none"
          data-testid={`studio-still-${c.id}`}
        />
      )}
      <span
        className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-colors"
        aria-hidden="true"
      >
        <span className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-slate-900 shadow-sm">
          {expanded ? <X className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
        </span>
      </span>
    </button>
  );
};

const ROW_BUTTON =
  'inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed';
const GATE_TITLE = 'Finish the commercial you are working on first';

/** Everyone's commercials except the one being worked on. (Not "Library":
    that is the advertiser's per-station file list, see CONTEXT.md.) */
const StudioList: React.FC<{
  studio: StudioCommercialsState;
  busy: boolean;
  onCustomize: (id: string) => void;
  onResume: (id: string) => void;
  onRestart: (id: string) => void;
  onNotice: (v: string | null) => void;
  onSpecWarning: (v: string | null) => void;
  onDownloadError: (v: string | null) => void;
}> = ({ studio, busy, onCustomize, onResume, onRestart, onNotice, onSpecWarning, onDownloadError }) => {
  // One inline preview open at a time; a plain <video>, so it never touches
  // the Waymark session and works while a generation runs.
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  if (!studio.isLoaded) return null;
  const rows = studio.commercials.filter((c) => c.id !== studio.activeId);
  if (rows.length === 0) {
    if (studio.active) return null;
    return (
      <section
        className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center mb-6"
        data-testid="studio-empty"
      >
        <Clapperboard className="w-6 h-6 text-slate-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-700">No commercials yet</p>
        <p className="mt-1 text-sm text-slate-500">
          Make one above and it will show up here.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="studio-list">
      <h2 className="text-base font-semibold text-slate-900">Commercials</h2>
      <p className="mt-1 mb-3 text-sm text-slate-500">
        Everyone's, newest first. A finished one stays here for good: Customize opens it in the
        editor again, Download hands over the broadcast file.
      </p>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {rows.map((c) => {
          const done = isDone(c);
          const customizable = done || (c.status === 'ready' && !!c.videoId);
          const previewing = previewId === c.id;
          return (
            <div key={c.id} className="px-4 py-3" data-testid={`studio-row-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <StudioThumb
                commercial={c}
                expanded={previewing}
                onToggle={() => setPreviewId((prev) => (prev === c.id ? null : c.id))}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                  <span className="truncate">{c.companyName ?? 'Untitled commercial'}</span>
                  {done && <DoneChip />}
                </div>
                <div className="text-xs text-slate-500">
                  <span
                    className={isInFlight(c) ? 'text-amber-700' : undefined}
                    data-testid={`studio-status-${c.id}`}
                  >
                    {libraryStatus(c)}
                  </span>
                  {' · '}
                  {madeBy(c)}
                </div>
                {c.errorMessage && !done && (
                  <div className="mt-0.5 text-xs text-slate-400 truncate" title={c.errorMessage}>
                    {c.errorMessage}
                  </div>
                )}
              </div>

              {customizable && (
                <button
                  type="button"
                  onClick={() => onCustomize(c.id)}
                  disabled={busy}
                  title={busy ? GATE_TITLE : undefined}
                  className={ROW_BUTTON}
                  data-testid={`studio-customize-${c.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Customize
                </button>
              )}

              {c.renderedUrl && (
                <DownloadButton
                  commercial={c}
                  renderedUrl={c.renderedUrl}
                  studio={studio}
                  onNotice={onNotice}
                  onSpecWarning={onSpecWarning}
                  onDownloadError={onDownloadError}
                  testId={`studio-download-${c.id}`}
                  variant="secondary"
                />
              )}

              {isInFlight(c) && (
                // Nothing outside the working card has a live SDK, so a row
                // in a working state here is stalled until someone resumes it.
                <button
                  type="button"
                  onClick={() => onResume(c.id)}
                  disabled={busy}
                  title={busy ? GATE_TITLE : undefined}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`studio-resume-${c.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Resume
                </button>
              )}

              {FAILED_STATUSES.has(c.status) && (
                <button
                  type="button"
                  onClick={() => onRestart(c.id)}
                  disabled={busy}
                  title={busy ? GATE_TITLE : undefined}
                  className={ROW_BUTTON}
                  data-testid={`studio-retry-${c.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Try again
                </button>
              )}

              {c.status === 'idle' && c.sourceUrl && (
                <button
                  type="button"
                  onClick={() => onRestart(c.id)}
                  disabled={busy}
                  title={busy ? GATE_TITLE : undefined}
                  className={ROW_BUTTON}
                  data-testid={`studio-start-${c.id}`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Start
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      `Remove "${c.companyName ?? 'this commercial'}" from the Studio? The file you already downloaded is unaffected.`,
                    )
                  ) {
                    void studio.remove(c.id);
                  }
                }}
                aria-label={`Remove ${c.companyName ?? 'commercial'} from the Studio`}
                title={`Remove ${c.companyName ?? 'this commercial'} from the Studio`}
                className="ml-2 rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                data-testid={`studio-remove-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {previewing && c.renderedUrl && (
              <div className="mt-3" data-testid={`studio-preview-${c.id}`}>
                {/* controlsList="nodownload": the native menu would save the
                    preview asset; the row's Download is the one that stamps
                    the export. autoPlay is fine here: it follows a click. */}
                <video
                  src={c.renderedUrl}
                  controls
                  controlsList="nodownload"
                  preload="metadata"
                  autoPlay
                  className="w-full max-w-xl rounded-lg border border-slate-200 bg-black"
                />
              </div>
            )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default StudioPage;
